import React from 'react';
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperienceTemplate,
  IExperienceTemplateResource,
  TemplateResourceType
} from '@dadohr/shared/domainTypes/experience';
import { Button, IconButton, Tooltip } from '@material-ui/core';
import { assertNever } from 'assert-never';
import { Settings } from 'lucide-react';
import { EditSpreadSheetDialog } from '../../features/Experiences/components/Builders/TaskBuilder/components/BlockSettings/SubTask/dataDestinations';
import { EditDocuSignEnvelopeResourceDialog } from '../../features/Experiences/components/Builders/TaskBuilder/components/BlockSettings/SubTask/DocuSignEnvelopeSign';
import { EditSalesforceResourceDialog } from '../../features/Experiences/components/Builders/TaskBuilder/components/BlockSettings/SubTask/EditSalesforceResourceDialog';
import { useSnackbar } from '../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../i18next';
import { FlexContainer } from '../../layout/Flex';
import { useCurrentUser } from '../../services/currentUser';
import { updateDataInDoc, updateDoc } from '../../services/firebase';
import { callFirebaseFunction } from '../../services/firebaseFunctions';
import {
  toGoogleDocLink,
  toGoogleDriveLink,
  toGoogleSheetsLink
} from '../../services/google';
import { now } from '../../services/time';

export const tplToDefaultTitle = (d: Doc<IExperienceTemplate>) =>
  `Dado - ${d.data.adminTitle || d.data.title}`;

const WithOptionalSettingsButton = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<{ onOpenSettings?: () => void }>
>(({ onOpenSettings, children }, ref) => {
  return (
    <FlexContainer ref={ref} spacing={0.5}>
      {children}
      {onOpenSettings && (
        <IconButton color="primary" onClick={() => onOpenSettings()}>
          <Settings size={16} />
        </IconButton>
      )}
    </FlexContainer>
  );
});

WithOptionalSettingsButton.displayName = 'WithOptionalSettingsButton';

export const Resource = ({
  d,
  defaultTitle = '',
  tooltipType = 'future',
  onOpenSettings
}: {
  d: IExperienceTemplateResource;
  defaultTitle?: string;
  tooltipType?: 'inProgress' | 'future';
  onOpenSettings?: () => void;
}) => {
  const { t } = useUnsafeTranslation();

  if (d.type === TemplateResourceType.GOOGLE_DOC_TEMPLATE) {
    return (
      <div>
        <Button
          rel="noopener"
          color="primary"
          size="small"
          href={toGoogleDocLink(d.docId)}
          target="_blank"
          startIcon={
            <img
              height={18}
              src="/images/resources/gdoc.svg"
              alt="Google Document"
            />
          }
        >
          {d.docTitle || defaultTitle}
        </Button>
      </div>
    );
  }

  if (d.type === 'GOOGLE_SHEET') {
    return (
      <div>
        <Tooltip
          title={
            d.sheetStatus !== 'COMPLETE'
              ? tooltipType === 'inProgress'
                ? t('resources.isGenerating') || ''
                : t('resources.willBeGenerated') || ''
              : ''
          }
        >
          <WithOptionalSettingsButton onOpenSettings={onOpenSettings}>
            <Button
              rel="noopener"
              color="primary"
              size="small"
              href={toGoogleSheetsLink(d.docId, d.sheetId)}
              target="_blank"
              disabled={d.sheetStatus !== 'COMPLETE'}
              startIcon={
                <img
                  height={18}
                  src="/images/resources/gsheet.svg"
                  alt="Google Sheet"
                />
              }
            >
              {d.docTitle || defaultTitle}
            </Button>
          </WithOptionalSettingsButton>
        </Tooltip>
      </div>
    );
  }

  if (d.type === TemplateResourceType.MS_EXCEL) {
    return (
      <div>
        <Tooltip
          title={
            d.status !== 'COMPLETE'
              ? tooltipType === 'inProgress'
                ? t('resources.isGenerating') || ''
                : t('resources.willBeGenerated') || ''
              : ''
          }
        >
          <WithOptionalSettingsButton onOpenSettings={onOpenSettings}>
            <Button
              rel="noopener"
              color="primary"
              size="small"
              href={d.webUrl}
              target="_blank"
              disabled={d.status !== 'COMPLETE'}
              startIcon={
                <img
                  height={18}
                  src="/images/resources/excel-logo.svg"
                  alt="Excel"
                />
              }
            >
              {d.docTitle || defaultTitle}
            </Button>
          </WithOptionalSettingsButton>
        </Tooltip>
      </div>
    );
  }

  if (d.type === 'GOOGLE_DRIVE_FOLDER') {
    return (
      <div>
        <Tooltip
          title={
            d.status !== 'COMPLETE'
              ? tooltipType === 'inProgress'
                ? t('resources.isGenerating') || ''
                : t('resources.willBeGenerated') || ''
              : ''
          }
        >
          <span>
            <Button
              rel="noopener"
              color="primary"
              size="small"
              href={toGoogleDriveLink(d.folderId)}
              target="_blank"
              disabled={d.status !== 'COMPLETE'}
              startIcon={
                <img src="/images/resources/gdrive.svg" alt="Google Drive" />
              }
            >
              {d.name || defaultTitle}
            </Button>
          </span>
        </Tooltip>
      </div>
    );
  }

  if (d.type === TemplateResourceType.MICROSOFT_SHAREPOINT_FOLDER) {
    const icon = (
      <img src="/images/resources/sharepoint.svg" alt="Microsoft Sharepoint" />
    );
    return (
      <div>
        <Tooltip
          title={
            d.status !== 'COMPLETE'
              ? tooltipType === 'inProgress'
                ? t('resources.isGenerating') || ''
                : t('resources.willBeGenerated') || ''
              : ''
          }
        >
          <span>
            <Button
              rel="noopener"
              color="primary"
              size="small"
              href={d.publicUrl}
              target="_blank"
              disabled={d.status !== 'COMPLETE'}
              startIcon={icon}
            >
              {`${d.sharepointSiteName}/${d.driveName}/${d.name || defaultTitle}`}
            </Button>
          </span>
        </Tooltip>
      </div>
    );
  }

  if (d.type === TemplateResourceType.MICROSOFT_ONEDRIVE_FOLDER) {
    const icon = (
      <img src="/images/resources/onedrive.svg" alt="Microsoft OneDrive" />
    );
    return (
      <div>
        <Tooltip
          title={
            d.status !== 'COMPLETE'
              ? tooltipType === 'inProgress'
                ? t('resources.isGenerating') || ''
                : t('resources.willBeGenerated') || ''
              : ''
          }
        >
          <span>
            <Button
              rel="noopener"
              color="primary"
              size="small"
              href={d.publicUrl}
              target="_blank"
              disabled={d.status !== 'COMPLETE'}
              startIcon={icon}
            >
              {d.name || defaultTitle}
            </Button>
          </span>
        </Tooltip>
      </div>
    );
  }

  if (d.type === TemplateResourceType.DOCUSIGN_ENVELOPE_TEMPLATE) {
    const icon = (
      <img src="/images/integrations-logos/docusign-icon.svg" alt="Docusign" />
    );
    return (
      <div>
        <Tooltip
          title={
            d.status !== 'COMPLETE' ? t('Please finish the setup') || '' : ''
          }
        >
          <Button
            color="primary"
            size="small"
            startIcon={icon}
            onClick={onOpenSettings}
          >
            {d.name || 'DocuSign Template'}
          </Button>
        </Tooltip>
      </div>
    );
  }

  if (d.type === TemplateResourceType.BACKGROUND_CHECK_PACKAGE) {
    return null;
  }

  if (d.type === TemplateResourceType.SALESFORCE) {
    return (
      <div>
        <Tooltip
          title={
            d.externalIdField
              ? `${t('Upserts')} ${d.objectName} ${t('by')} ${d.externalIdField}`
              : t('Salesforce destination — setup incomplete') || ''
          }
        >
          <WithOptionalSettingsButton onOpenSettings={onOpenSettings}>
            <Button
              color="primary"
              size="small"
              onClick={onOpenSettings}
              startIcon={
                <img
                  height={18}
                  src="/images/integrations-logos/salesforce.svg"
                  alt="Salesforce"
                />
              }
            >
              {d.objectName || defaultTitle || 'Salesforce'}
            </Button>
          </WithOptionalSettingsButton>
        </Tooltip>
      </div>
    );
  }

  // SALESFORCE_CONTACT is an instance-side resource (populated per-experience
  // by the participant-upsert worker at launch). It shouldn't appear on
  // template-edit pages, but the type is part of IExperienceTemplateResource —
  // render nothing rather than crash if one slips through.
  if (d.type === TemplateResourceType.SALESFORCE_CONTACT) {
    return null;
  }

  return assertNever(d);
};

// Bit weird mechanics, but as this dialog also determines whether the resource
// can actually be edited, we do it this way:
// - Pass a resource to potentially open it
// - Pass a null resource to definitely close it
export const ResourceEditDialog = ({
  d,
  resource,
  onClose
}: {
  d: Doc<IExperienceTemplate>;
  resource: IExperienceTemplateResource | null;
  onClose: () => void;
}) => {
  const cU = useCurrentUser();
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();
  if (!resource) {
    return null;
  }

  const updateResourceInDoc = async (
    nextResource: IExperienceTemplateResource
  ) => {
    const nextDoc = updateDataInDoc(d, {
      resources: d.data.resources.map((r) =>
        r.resourceId === nextResource.resourceId ? nextResource : r
      )
    });

    await updateDoc(nextDoc, (x) => ({
      updatedAt: now(),
      updatedBy: cU.id,
      resources: x.resources
    }))
      .then(() => {
        enqueueSnackbarSuccess('Resource updated!');
      })
      .catch((err) => {
        console.error('Failed to update resource', err);
        enqueueSnackbarError('Failed to update resource');
      });
  };

  if (resource.type === TemplateResourceType.DOCUSIGN_ENVELOPE_TEMPLATE) {
    return (
      <EditDocuSignEnvelopeResourceDialog
        open={true}
        onClose={onClose}
        orgId={cU.org.id}
        customFields={cU.org.customUserFields}
        supportingActors={d.data.supportingActors}
        value={resource}
        mode="edit"
        onChangeAsync={(nextResource) => {
          // Mind that this could render subtasks invalid - probably
          // need to shield against that.
          return updateResourceInDoc(nextResource);
        }}
      />
    );
  }

  if (
    resource.type === TemplateResourceType.GOOGLE_SHEET ||
    resource.type === TemplateResourceType.MS_EXCEL
  ) {
    return (
      <EditSpreadSheetDialog
        open={true}
        onClose={onClose}
        value={resource}
        onChangeAsync={updateResourceInDoc}
        onRequestAccess={async () => {
          await callFirebaseFunction('experiences-shareResourceWithUser', {
            orgId: d.data.orgId,
            tplId: d.id,
            resourceId: resource.resourceId,
            adminUserId: cU.id
          }).then(
            () => {
              enqueueSnackbarSuccess('Access granted!');
            },
            (err) => {
              console.log('RESOURCE_GRANT_ERR', err);
              enqueueSnackbarError('Failed to grant access');
            }
          );
        }}
        customFields={cU.org.customUserFields}
        mode="update"
      />
    );
  }

  if (resource.type === TemplateResourceType.SALESFORCE) {
    return (
      <EditSalesforceResourceDialog
        open={true}
        onClose={onClose}
        orgId={cU.org.id}
        value={resource}
        onChange={(nextResource) => {
          // Fire-and-forget; the dialog's own snackbar handling is via the
          // updateResourceInDoc promise's .then/.catch above.
          void updateResourceInDoc(nextResource);
        }}
      />
    );
  }

  return null;
};
